// @ts-nocheck
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';

import { abaTherapyServicesActions, treatmentPlanActions } from '../../../../../store';
import { TherapyServiceSchema } from '../../therapy-history/validationSchema';
import { getAllInvalidFieldNamesAuto } from '../../../../../helpers';
import { TreatmentPlanPages } from '../../constants/general';

import type { RootState } from '../../../../../store';
import type { IAbaTherapy, IAbaTherapyService } from '../../../../../utils/types';

function useValidateAbaTherapyHistory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { current: treatmentPlan } = useSelector((state: RootState) => state.treatmentPlans);
  const { selected: selectedClient } = useSelector((state: RootState) => state.clients);

  const {
    therapyHistory,
    services,
    treatmentPlanId: currentTPId,
  } = useSelector((state: RootState) => state.abaTherapyServices);

  const planId = treatmentPlan?.id;

  const [treatmentPlanId, setTreatmentPlanId] = useState<number | null>(null);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [fetchComplete, setFetchComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!planId) return;
    setTreatmentPlanId(planId);
  }, [planId]);

  useEffect(() => {
    if (!treatmentPlanId || !selectedClient?.id) return;
    dispatch(
      abaTherapyServicesActions.updateState({ key: 'triggerValidationsOnPageLoad', data: true }),
    );
    if (currentTPId === treatmentPlanId) {
      const abaTherapyHistoryData = therapyHistory?.id ? therapyHistory : null;
      fetchAndValidateData(abaTherapyHistoryData, treatmentPlanId);
    } else {
      dispatch(abaTherapyServicesActions.resetState());
      fetchAndValidateData(null, treatmentPlanId);
    }
    dispatch(
      abaTherapyServicesActions.updateState({ key: 'treatmentPlanId', data: treatmentPlanId }),
    );
  }, [treatmentPlanId, selectedClient]);

  useEffect(() => {
    if (!treatmentPlanId || !fetchComplete) return;
    validate(therapyHistory, services);
  }, [therapyHistory, services, fetchComplete]);

  async function fetchAndValidateData(
    abaTherapyHistoryData: IAbaTherapy | Partial<IAbaTherapy> | null,
    treatmentPlanId: number,
  ) {
    setFetchComplete(false);
    if (!abaTherapyHistoryData?.id) {
      await dispatch(
        abaTherapyServicesActions.getAbaTherapy({
          treatmentPlanId: treatmentPlanId,
          getAccessTokenSilently,
        }),
      );
    }
    setFetchComplete(true);
  }

  const validate = (
    therapyHistory: IAbaTherapy | Partial<IAbaTherapy> | null,
    services: Array<IAbaTherapyService>,
  ) => {
    let errorCount = 0;
    if (!therapyHistory || Object.keys(therapyHistory).length === 0) {
      errorCount++;
    }
    if (therapyHistory?.id) {
      if (
        (therapyHistory['has_service_history'] || therapyHistory['has_service_history'] === null) &&
        services?.length > 0
      ) {
        const allValidationErrors: Record<number, Record<string, boolean>> = {};
        services.forEach((abaService: IAbaTherapyService, index: number) => {
          const validationErrors = getAllInvalidFieldNamesAuto(TherapyServiceSchema, abaService);
          allValidationErrors[index] = validationErrors;
        });
        Object.keys(allValidationErrors).forEach((rowIndex) =>
          Object.keys(allValidationErrors[Number(rowIndex)]).forEach((rowObjectKey) => {
            if (allValidationErrors[Number(rowIndex)][rowObjectKey]) {
              errorCount++;
            }
          }),
        );
      } else if (
        (therapyHistory['has_service_history'] || therapyHistory['has_service_history'] === null) &&
        services?.length === 0
      ) {
        errorCount++;
      }
    }

    setIsValid(errorCount === 0 ? true : false);

    dispatch(
      treatmentPlanActions.updateBadge({
        [TreatmentPlanPages.History]: errorCount,
      }),
    );
  };
  return isValid && fetchComplete;
}

export default useValidateAbaTherapyHistory;
